<script setup>
import { computed } from 'vue';
import { getI18nKey } from 'dashboard/routes/dashboard/settings/helper/settingsHelper';
import ShowMore from 'dashboard/components/widgets/ShowMore.vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  webhook: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'toggle', 'ai-training']);

const isAiWebhook = computed(() => {
  const name = (props.webhook.name || '').toLowerCase().trim();
  return name === 'ia ksys customizada';
});
const { t } = useI18n();
const subscribedEvents = computed(() => {
  const { subscriptions } = props.webhook;
  return subscriptions
    .map(event =>
      t(
        getI18nKey(
          'INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS',
          event
        )
      )
    )
    .join(', ');
});
</script>

<template>
  <tr>
    <td class="py-4 ltr:pr-4 rtl:pl-4">
      <div class="flex gap-2 font-medium break-words text-n-slate-12">
        <template v-if="webhook.name">
          {{ webhook.name }}
          <span class="text-n-slate-11">
            {{ webhook.url }}
          </span>
        </template>
        <template v-else>
          {{ webhook.url }}
        </template>
      </div>
      <div class="block mt-1 text-sm text-n-slate-11">
        <span class="font-medium">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.SUBSCRIBED_EVENTS') }}:
        </span>
        <ShowMore :text="subscribedEvents" :limit="60" />
      </div>
    </td>
    <td class="py-4 min-w-xs">
      <div class="flex items-center justify-end gap-2">
        <Button
          v-if="isAiWebhook"
          v-tooltip.top="'Treinamento da IA'"
          icon="i-lucide-brain"
          xs
          blue
          faded
          @click="emit('ai-training', webhook)"
        />
        <button
          v-tooltip.top="
            webhook.active
              ? $t('INTEGRATION_SETTINGS.WEBHOOK.TOGGLE.DISABLE')
              : $t('INTEGRATION_SETTINGS.WEBHOOK.TOGGLE.ENABLE')
          "
          class="toggle-switch"
          :class="{ 'toggle-switch--active': webhook.active }"
          role="switch"
          :aria-checked="webhook.active"
          @click="emit('toggle', webhook)"
        >
          <span class="toggle-switch__knob" />
        </button>
        <Button
          v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.BUTTON_TEXT')"
          icon="i-lucide-pen"
          slate
          xs
          faded
          @click="emit('edit', webhook)"
        />
        <Button
          v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.BUTTON_TEXT')"
          icon="i-lucide-trash-2"
          xs
          ruby
          faded
          @click="emit('delete', webhook, index)"
        />
      </div>
    </td>
  </tr>
</template>

<style scoped>
.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 9999px;
  background-color: #d1d5db;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
}

.toggle-switch:hover {
  background-color: #b0b5be;
}

.toggle-switch--active {
  background-color: #22c55e;
}

.toggle-switch--active:hover {
  background-color: #16a34a;
}

.toggle-switch__knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toggle-switch--active .toggle-switch__knob {
  transform: translateX(20px);
}
</style>
